import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { LayoutDashboard, LogOut, Menu, X } from "lucide-react";

export function Logo({ className = "" }) {
  return (
    <Link to="/" data-testid="logo-link" className={`flex items-center gap-2 ${className}`}>
      <div className="flex items-center">
        <span className="text-2xl font-extrabold tracking-tight text-white">LO</span>
        <span className="w-4 h-4 rounded-full border-[3px] border-[#C5A059] mx-[1px] mb-[2px]" />
        <span className="text-2xl font-extrabold tracking-tight text-white">P</span>
      </div>
    </Link>
  );
}

const dashPath = (role) => (role === "admin" ? "/admin" : role === "owner" ? "/owner" : "/buyer");

export default function Header() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const links = [
    { to: "/search?listing_type=buy", label: "Buy" },
    { to: "/search?listing_type=rent", label: "Rent" },
    { to: "/search?property_type=commercial", label: "Commercial" },
    { to: "/search?property_type=plot", label: "Plots" },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#070C1E]/85 border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 h-16 flex items-center justify-between">
        <div className="flex items-center gap-10">
          <Logo />
          <nav className="hidden lg:flex items-center gap-7">
            {links.map((l) => (
              <Link key={l.label} to={l.to} data-testid={`nav-${l.label.toLowerCase()}`}
                className="text-sm text-slate-300 hover:text-[#C5A059] transition-colors">
                {l.label}
              </Link>
            ))}
          </nav>
        </div>

        <div className="hidden md:flex items-center gap-3">
          <Button data-testid="header-list-property-btn" variant="ghost"
            onClick={() => nav(user && user.role === "owner" ? "/list-property" : "/owner")}
            className="text-slate-200 hover:text-white hover:bg-white/5">
            List Your Property
          </Button>
          {user ? (
            <div className="flex items-center gap-2">
              <Button data-testid="header-dashboard-btn" onClick={() => nav(dashPath(user.role))}
                className="bg-[#C5A059] hover:bg-[#b18f47] text-[#070C1E] font-semibold">
                <LayoutDashboard className="w-4 h-4 mr-2" /> Dashboard
              </Button>
              <Button data-testid="header-logout-btn" variant="ghost" size="icon"
                onClick={() => { logout(); nav("/"); }} className="text-slate-300 hover:text-white">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <Button data-testid="header-signin-btn" onClick={() => nav("/auth")}
              className="bg-[#C5A059] hover:bg-[#b18f47] text-[#070C1E] font-semibold">
              Sign In
            </Button>
          )}
        </div>

        <button data-testid="mobile-menu-toggle" className="md:hidden text-white" onClick={() => setOpen(!open)}>
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-[#0B132B] border-t border-white/10 px-6 py-4 space-y-3">
          {links.map((l) => (
            <Link key={l.label} to={l.to} onClick={() => setOpen(false)}
              className="block text-slate-200 py-1">{l.label}</Link>
          ))}
          <div className="pt-3 border-t border-white/10 flex flex-col gap-2">
            {user ? (
              <>
                <Button onClick={() => { nav(dashPath(user.role)); setOpen(false); }}
                  className="bg-[#C5A059] text-[#070C1E]">Dashboard</Button>
                <Button variant="ghost" onClick={() => { logout(); nav("/"); setOpen(false); }}>Logout</Button>
              </>
            ) : (
              <Button onClick={() => { nav("/auth"); setOpen(false); }} className="bg-[#C5A059] text-[#070C1E]">Sign In</Button>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
